import {defineStore} from 'pinia'
import {ref} from 'vue'
import type {LocationQuery} from 'vue-router'
import type {UtmData} from '@/modules/marketing/types'
import {
    UTM_LANDING_URL_MAX_LENGTH,
    UTM_ORDER_ATTR_CODES,
    UTM_ORDER_FIELD_MAX_LENGTH,
    UTM_QUERY_PREFIX,
} from '@/modules/marketing/types'

const STORE_NAME = 'marketing-utm'

function queryValueToString(value: LocationQuery[string]): string | null {
    if (value == null) {
        return null
    }

    const raw = Array.isArray(value) ? value[0] : value
    if (raw == null || raw === '') {
        return null
    }

    return String(raw)
}

function truncateField(value: string, max = UTM_ORDER_FIELD_MAX_LENGTH): string {
    if (value.length <= max) {
        return value
    }
    return value.slice(0, max)
}

export const useUtmStore = defineStore(STORE_NAME, () => {
    const tags = ref<UtmData | null>(null)

    function captureFromUrl(query: LocationQuery, landingUrl: string) {
        const utmParams: Record<string, string> = {}
        let hasUtm = false

        for (const [key, value] of Object.entries(query)) {
            if (!key.startsWith(UTM_QUERY_PREFIX)) {
                continue
            }

            const normalized = queryValueToString(value)
            if (!normalized) {
                continue
            }

            hasUtm = true
            utmParams[key] = normalized
        }

        if (!hasUtm) {
            return
        }

        tags.value = {
            ...utmParams,
            captured_at: Math.floor(Date.now() / 1000),
            landing_url: landingUrl,
        } as UtmData
    }

    /** Полный объект для dbg-panel / persist */
    function getPayload(): UtmData | null {
        if (!tags.value) {
            return null
        }

        return {...tags.value}
    }

    /**
     * Атрибуты заказа для submit: отдельные UTM_* свойства Bitrix.
     * Заказ считается рекламным, если заполнено хотя бы одно поле.
     */
    function getOrderAttrs(): Record<string, string> | null {
        if (!tags.value) {
            return null
        }

        const attrs: Record<string, string> = {}

        for (const [field, code] of Object.entries(UTM_ORDER_ATTR_CODES)) {
            const raw = tags.value[field as keyof UtmData]
            if (raw == null) {
                continue
            }

            const maxLength = field === 'landing_url'
                ? UTM_LANDING_URL_MAX_LENGTH
                : UTM_ORDER_FIELD_MAX_LENGTH
            const value = truncateField(String(raw).trim(), maxLength)
            if (!value) {
                continue
            }

            attrs[code] = value
        }

        return Object.keys(attrs).length ? attrs : null
    }

    function reset() {
        tags.value = null
    }

    return {
        tags,
        captureFromUrl,
        getPayload,
        getOrderAttrs,
        reset,
    }
}, {
    persist: {
        pick: ['tags'],
    },
})
